import { ref, toValue } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user'
import config from '@/config'

/**
 * Composable для экспорта списков в Excel
 *
 * @param {import('vue').Ref|Function|String} modelName - Имя модели (activities, orders и т.д.)
 * @param {import('vue').Ref|Function|Array} columns - Конфигурация колонок для экспорта
 * @param {import('vue').Ref|Function|Object} filters - Параметры фильтрации
 * @returns {{ exportToExcel: Function, pending: import('vue').Ref, error: import('vue').Ref }}
 */
export function useExcelExport(modelName, columns, filters) {
  const pending = ref(false)
  const error = ref(null)
  const { user, isAuthorized } = storeToRefs(useUserStore())

  /**
   * Выполняет экспорт списка в Excel
   */
  async function exportToExcel() {
    try {
      pending.value = true
      error.value = null

      const payload = {
        model_name: toValue(modelName),
        columns: toValue(columns)
          .filter(c => c.export !== false)
          .map(c => {
            const col = {
              title: c.title,
              label: c.label,
              path: c.export_path || c.path || c.title,
            }
            if (c.paths) col.paths = c.paths
            if (c.value_map) col.value_map = c.value_map
            if (c.value_compute) col.value_compute = c.value_compute
            return col
          }),
        filters: {
          ...toValue(filters),
          page: 1,
          onPage: 10000, // Экспортируем все записи (до 10000)
        },
      }

      // Используем fetch напрямую для POST запроса
      const headers = {
        'Content-Type': 'application/json',
      }

      // Добавляем Bearer токен если авторизован
      if (isAuthorized.value && user.value?.access_token) {
        headers.Authorization = `Bearer ${user.value.access_token}`
      }

      const response = await fetch(`${config.VITE_API_URL}export/excel`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      // Получаем файл как blob и скачиваем
      const blob = await response.blob()
      const disposition = response.headers.get('Content-Disposition')
      let filename = `${toValue(modelName)}_export.xlsx`
      if (disposition) {
        const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/)
        if (match) filename = decodeURIComponent(match[1])
      }

      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      error.value = e
      console.error('Excel export error:', e)
      throw e
    } finally {
      pending.value = false
    }
  }

  return {
    exportToExcel,
    pending,
    error,
  }
}
